import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { Form, Button, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { validateKeywords, parseKeywords, MAX_KEYWORDS } from '../../utils/keywordValidator';
import './BookArticlesForm.css';

const config = require('../../config.json');

const ARTICLE_STATUS_OPTIONS = ['Active', 'Draft', 'Archived', 'Under Review', 'Inactive'];

export default function BookArticlesForm(props) {
  const { enqueueSnackbar } = useSnackbar();
  const { loggedUser } = useContext(ProductContext);

  const {
    viewOnly = false,
    formType = 'New',
    handleClose,
    existingArticles = [],
    onSuccess,
    oldArticle = {},
    bookDetails = {},
    bookData = {},
    articleData: externalArticleData,
    setArticleData: externalSetArticleData,
    accessionNo: propAccessionNo = '',
    bookTitle: propBookTitle = '',
    callNo: propCallNo = ''
  } = props;

  const isViewMode = formType === 'View' || viewOnly;
  const isEditMode = formType === 'Edit';
  const isNewMode = formType === 'New' && !viewOnly;

  // Active book context from props or existing article
  const activeAccessionNo =
    externalArticleData?.accessionNo ||
    bookDetails?.accessionNo ||
    bookData?.accessionNo ||
    propAccessionNo ||
    '';

  const activeBookTitle =
    externalArticleData?.bookTitle ||
    bookDetails?.bookTitle ||
    bookData?.bookTitle ||
    propBookTitle ||
    '';

  const activeCallNo =
    externalArticleData?.callNo ||
    bookDetails?.bookClassificationNo ||
    bookDetails?.callNo ||
    bookData?.callNo ||
    propCallNo ||
    '';

  // Local state for article fields (fallback if external state is not provided)
  const [localFormData, setLocalFormData] = useState({
    clientID: externalArticleData?.clientID || loggedUser?.website || 'VJCollege',
    articleID: externalArticleData?.articleID || '',
    accessionNo: activeAccessionNo,
    bookTitle: activeBookTitle,
    articleTitle: externalArticleData?.articleTitle || '',
    articleSubTitle: externalArticleData?.articleSubTitle || '',
    articleAuthor: externalArticleData?.articleAuthor || '',
    articleKeywords: externalArticleData?.articleKeywords || '',
    callNo: activeCallNo,
    articleStatus: externalArticleData?.articleStatus || 'Active',
    articleDescription: externalArticleData?.articleDescription || '',
    startPageNo: externalArticleData?.startPageNo || '',
    endPageNo: externalArticleData?.endPageNo || '',
    updatedOn: externalArticleData?.updatedOn || '',
    updatedBy: externalArticleData?.updatedBy || ''
  });

  // Effective state combining external or local state
  const currentData = externalArticleData || localFormData;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingId, setIsGeneratingId] = useState(false);
  const [generatedSeq, setGeneratedSeq] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Extract sequence number from articleID string given the book accession number
  const extractSequenceNumber = useCallback((idStr, targetAccession) => {
    if (!idStr) return null;
    const str = String(idStr).trim();

    // 1. Match exact `${targetAccession}-<num>` or `${targetAccession}_<num>` (with optional zero-padding)
    if (targetAccession) {
      const escapedAcc = String(targetAccession).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixRegex = new RegExp(`^${escapedAcc}[-_](\\d+)$`, 'i');
      const matchPrefix = str.match(prefixRegex);
      if (matchPrefix && matchPrefix[1]) {
        return parseInt(matchPrefix[1], 10);
      }
    }

    // 2. Match general trailing suffix `...-<num>` or `..._<num>`
    const suffixMatch = str.match(/[-_](\\d+)$/);
    if (suffixMatch && suffixMatch[1]) {
      return parseInt(suffixMatch[1], 10);
    }

    // 3. Match pure integer string `"1"`, `"001"`, etc.
    if (/^\d+$/.test(str)) {
      return parseInt(str, 10);
    }

    return null;
  }, []);

  // Set field data in state (handles both external state setter and local state)
  const setFormData = useCallback((updater) => {
    if (externalSetArticleData) {
      externalSetArticleData(updater);
    }
    setLocalFormData(updater);
  }, [externalSetArticleData]);

  // Auto-generate next sequential unique articleID (e.g. accessionNo-001) for New formType
  const generateSequentialArticleID = useCallback(async (targetAccession) => {
    const acc = targetAccession || activeAccessionNo;
    if (!acc || String(acc).trim() === '') {
      const fallbackID = 'ART-001';
      setGeneratedSeq(1);
      setFormData(prev => ({
        ...prev,
        articleID: prev.articleID || fallbackID
      }));
      return;
    }

    const cleanAccession = String(acc).trim().toUpperCase();
    setIsGeneratingId(true);

    try {
      let articlesList = Array.isArray(existingArticles) && existingArticles.length > 0
        ? [...existingArticles]
        : [];

      // Query backend to retrieve existing articles for this book accession number if available
      try {
        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        const clientID = loggedUser?.website || 'VJCollege';

        // Query search endpoint for catalogue book articles
        const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books/articles`, {
          params: {
            fetchKey: cleanAccession,
            fetchOption: 'AccessionNo',
            clientID: clientID
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        if (Array.isArray(response.data)) {
          const fetched = response.data;
          const map = new Map();
          articlesList.forEach(a => {
            const key = a.articleID || a.accessionNo;
            if (key) map.set(key, a);
          });
          fetched.forEach(a => {
            if (a.articleID) map.set(a.articleID, a);
          });
          articlesList = Array.from(map.values());
        }
      } catch (fetchErr) {
        console.warn('Could not query backend for article sequence check, using local list:', fetchErr);
      }

      // Filter articles belonging to this book accession number
      const relevantArticles = cleanAccession
        ? articlesList.filter(a =>
            !a.accessionNo || String(a.accessionNo).trim().toUpperCase() === cleanAccession
          )
        : articlesList;

      // Extract all sequence numbers
      const sequences = relevantArticles
        .map(a => extractSequenceNumber(a.articleID, cleanAccession))
        .filter(n => n !== null && !isNaN(n) && n > 0);

      const maxSeq = sequences.length > 0 ? Math.max(...sequences) : 0;
      const nextSeq = maxSeq + 1;
      const formattedSeq = String(nextSeq).padStart(3, '0');
      const newArticleID = `${cleanAccession}-${formattedSeq}`;

      setGeneratedSeq(nextSeq);
      setFormData(prev => ({
        ...prev,
        accessionNo: cleanAccession,
        articleID: newArticleID,
        bookTitle: prev.bookTitle || activeBookTitle || '',
        callNo: prev.callNo || activeCallNo || '',
        articleStatus: prev.articleStatus || 'Active'
      }));
    } catch (err) {
      console.error('Error generating sequential article ID:', err);
      const fallbackSeq = 1;
      const fallbackID = `${cleanAccession}-001`;
      setGeneratedSeq(fallbackSeq);
      setFormData(prev => ({
        ...prev,
        accessionNo: cleanAccession,
        articleID: fallbackID,
        bookTitle: prev.bookTitle || activeBookTitle || '',
        callNo: prev.callNo || activeCallNo || '',
        articleStatus: prev.articleStatus || 'Active'
      }));
    } finally {
      setIsGeneratingId(false);
    }
  }, [activeAccessionNo, activeBookTitle, activeCallNo, existingArticles, extractSequenceNumber, loggedUser, setFormData]);

  // Initial synchronization and auto-generation on mode change
  useEffect(() => {
    if (isNewMode) {
      const acc = currentData?.accessionNo || activeAccessionNo;
      generateSequentialArticleID(acc);
    } else {
      setFormData(prev => ({
        ...prev,
        accessionNo: prev.accessionNo || activeAccessionNo || '',
        bookTitle: prev.bookTitle || activeBookTitle || '',
        callNo: prev.callNo || activeCallNo || '',
        articleStatus: prev.articleStatus || 'Active'
      }));
    }
  }, [isNewMode, formType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live Page Range Calculation
  const pageStats = useMemo(() => {
    const start = parseInt(currentData?.startPageNo, 10);
    const end = parseInt(currentData?.endPageNo, 10);

    if (!isNaN(start) && !isNaN(end)) {
      if (end >= start) {
        return {
          valid: true,
          count: end - start + 1,
          message: `${end - start + 1} Pages (pp. ${start}–${end})`
        };
      } else {
        return {
          valid: false,
          count: 0,
          message: 'End page must be greater than or equal to start page'
        };
      }
    }
    return null;
  }, [currentData?.startPageNo, currentData?.endPageNo]);

  // Live Keyword Tags List
  const keywordTags = useMemo(() => {
    return parseKeywords(currentData?.articleKeywords);
  }, [currentData?.articleKeywords]);

  // Form Field Change Handler
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // If accessionNo changed in New mode, regenerate sequence ID
    if (field === 'accessionNo' && isNewMode && value.trim()) {
      generateSequentialArticleID(value.trim());
    }

    // Clear error for this field if resolved
    if (formErrors[field]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  // Form Validation
  const validateForm = () => {
    const errors = {};

    if (!currentData?.accessionNo || String(currentData.accessionNo).trim() === '') {
      errors.accessionNo = 'Book Accession No is required.';
    }

    if (!currentData?.articleID || String(currentData.articleID).trim() === '') {
      errors.articleID = 'Article ID is required (auto-generated).';
    }

    if (!currentData?.bookTitle || String(currentData.bookTitle).trim() === '') {
      errors.bookTitle = 'Book Title is required.';
    }

    if (!currentData?.articleTitle || String(currentData.articleTitle).trim() === '') {
      errors.articleTitle = 'Article Title is required.';
    }

    if (!currentData?.articleAuthor || String(currentData.articleAuthor).trim() === '') {
      errors.articleAuthor = 'Article Author is required.';
    }

    if (!currentData?.callNo || String(currentData.callNo).trim() === '') {
      errors.callNo = 'Call No / Classification No is required.';
    }

    const kwValidation = validateKeywords(currentData?.articleKeywords, true);
    if (!kwValidation.valid) {
      errors.articleKeywords = kwValidation.error;
    }

    if (!currentData?.startPageNo || String(currentData.startPageNo).trim() === '') {
      errors.startPageNo = 'Start Page No is required.';
    } else if (isNaN(parseInt(currentData.startPageNo, 10)) || parseInt(currentData.startPageNo, 10) < 1) {
      errors.startPageNo = 'Must be a valid positive number.';
    }

    if (!currentData?.endPageNo || String(currentData.endPageNo).trim() === '') {
      errors.endPageNo = 'End Page No is required.';
    } else if (isNaN(parseInt(currentData.endPageNo, 10)) || parseInt(currentData.endPageNo, 10) < 1) {
      errors.endPageNo = 'Must be a valid positive number.';
    }

    if (!errors.startPageNo && !errors.endPageNo) {
      const s = parseInt(currentData.startPageNo, 10);
      const e = parseInt(currentData.endPageNo, 10);
      if (e < s) {
        errors.endPageNo = 'End page cannot be less than start page.';
      }
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrMsg = Object.values(errors)[0];
      enqueueSnackbar(`Validation Error: ${firstErrMsg}`, { variant: 'error' });
      return false;
    }
    return true;
  };

  // Submit Handler (POST /vjlibrary/catalogue/articles)
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const payload = {
        clientID: loggedUser?.website || currentData.clientID || 'VJCollege',
        articleID: String(currentData.articleID || '').trim(),
        accessionNo: String(currentData.accessionNo || activeAccessionNo || '').trim().toUpperCase(),
        bookTitle: String(currentData.bookTitle || activeBookTitle || '').trim().toUpperCase(),
        articleTitle: String(currentData.articleTitle || '').trim().toUpperCase(),
        articleSubTitle: String(currentData.articleSubTitle || '').trim().toUpperCase(),
        articleAuthor: String(currentData.articleAuthor || '').trim().toUpperCase(),
        articleKeywords: String(currentData.articleKeywords || '').trim().toUpperCase(),
        callNo: String(currentData.callNo || activeCallNo || '').trim().toUpperCase(),
        articleStatus: String(currentData.articleStatus || 'Active').trim(),
        articleDescription: String(currentData.articleDescription || '').trim(),
        startPageNo: String(currentData.startPageNo || '').trim(),
        endPageNo: String(currentData.endPageNo || '').trim(),
        updatedOn: new Date().toISOString(),
        updatedBy: loggedUser?.nickname || loggedUser?.name || 'Librarian'
      };

      await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/articles`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      // Index keywords if backend keyword indexing endpoint is available
      try {
        const keywordpayload = {
          article: payload,
          oldArticle: ''
        };
        await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/articles/keyword`, keywordpayload, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });
      } catch (kwErr) {
        console.warn('Keyword index hook notification (optional):', kwErr);
      }

      enqueueSnackbar(`Article "${payload.articleID}" created successfully!`, { variant: 'success' });
      if (onSuccess) onSuccess(payload);
      if (handleClose) handleClose();
    } catch (error) {
      console.error('Error submitting book article:', error);
      enqueueSnackbar(`Error: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update Handler (PATCH /vjlibrary/catalogue/articles)
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const payload = {
        clientID: loggedUser?.website || currentData.clientID || 'VJCollege',
        articleID: String(currentData.articleID || '').trim(),
        accessionNo: String(currentData.accessionNo || activeAccessionNo || '').trim().toUpperCase(),
        bookTitle: String(currentData.bookTitle || activeBookTitle || '').trim().toUpperCase(),
        articleTitle: String(currentData.articleTitle || '').trim().toUpperCase(),
        articleSubTitle: String(currentData.articleSubTitle || '').trim().toUpperCase(),
        articleAuthor: String(currentData.articleAuthor || '').trim().toUpperCase(),
        articleKeywords: String(currentData.articleKeywords || '').trim().toUpperCase(),
        callNo: String(currentData.callNo || activeCallNo || '').trim().toUpperCase(),
        articleStatus: String(currentData.articleStatus || 'Active').trim(),
        articleDescription: String(currentData.articleDescription || '').trim(),
        startPageNo: String(currentData.startPageNo || '').trim(),
        endPageNo: String(currentData.endPageNo || '').trim(),
        updatedOn: new Date().toISOString(),
        updatedBy: loggedUser?.nickname || loggedUser?.name || 'Librarian'
      };

      await axios.patch(`${config.api.invokeURL}/vjlibrary/catalogue/articles`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      // Update keyword indexing if keywords changed
      if (oldArticle && oldArticle.articleKeywords !== currentData.articleKeywords) {
        try {
          const keywordpayload = {
            article: payload,
            oldArticle: oldArticle
          };
          await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/articles/keyword`, keywordpayload, {
            headers: {
              Authorization: idToken,
              'x-api-key': config.api.key
            }
          });
        } catch (kwErr) {
          console.warn('Keyword index update hook notification (optional):', kwErr);
        }
      }

      enqueueSnackbar(`Article "${payload.articleID}" updated successfully!`, { variant: 'success' });
      if (onSuccess) onSuccess(payload);
      if (handleClose) handleClose();
    } catch (error) {
      console.error('Error updating book article:', error);
      enqueueSnackbar(`Error: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="book-articles-form-container">
      {/* Context Summary Header Banner */}
      <div className="book-article-context-banner">
        <div>
          <div className="book-article-context-title">
            <i className={isViewMode ? 'fa fa-book-open' : isEditMode ? 'fa fa-edit' : 'fa fa-file-circle-plus'}></i>
            {isViewMode ? 'Book Article Overview' : isEditMode ? 'Edit Book / Catalogue Article' : 'New Book / Catalogue Article'}
          </div>
          <div className="book-article-context-subtitle">
            <i className="fa fa-book"></i>
            {currentData?.bookTitle ? currentData.bookTitle : activeBookTitle || 'Catalogue Collection Item'}
          </div>
        </div>
        <div className="book-article-context-meta">
          {(currentData?.accessionNo || activeAccessionNo) && (
            <span className="book-context-meta-pill" title="Book Accession Number">
              <i className="fa fa-barcode"></i> Acc: {currentData?.accessionNo || activeAccessionNo}
            </span>
          )}
          {currentData?.articleStatus && (
            <span className="book-context-meta-pill status-pill" title="Article Status">
              <i className="fa fa-circle-check"></i> {currentData.articleStatus}
            </span>
          )}
          {currentData?.articleID && (
            <span className="book-context-meta-pill id-pill" title="Incremental Unique Article ID">
              <i className="fa fa-fingerprint"></i> ID: {currentData.articleID}
            </span>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VIEW ONLY READOUT                                                         */}
      {/* ========================================================================= */}
      {isViewMode ? (
        <div className="book-article-view-card">
          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-key text-primary"></i> Article ID</div>
            <div className="book-article-view-value">
              <span className="badge bg-primary px-3 py-2 fs-6 font-monospace">{currentData?.articleID || 'N/A'}</span>
            </div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-barcode text-primary"></i> Accession No</div>
            <div className="book-article-view-value fw-semibold text-dark">{currentData?.accessionNo || 'N/A'}</div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-book text-primary"></i> Book Title</div>
            <div className="book-article-view-value fw-bold text-dark">{currentData?.bookTitle || 'N/A'}</div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-heading text-primary"></i> Article Title</div>
            <div className="book-article-view-value fw-bold text-primary">{currentData?.articleTitle || 'N/A'}</div>
          </div>

          {currentData?.articleSubTitle && (
            <div className="book-article-view-row">
              <div className="book-article-view-label"><i className="fa fa-align-left text-secondary"></i> Subtitle</div>
              <div className="book-article-view-value text-secondary">{currentData.articleSubTitle}</div>
            </div>
          )}

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-user-edit text-primary"></i> Article Author(s)</div>
            <div className="book-article-view-value text-dark fw-semibold">{currentData?.articleAuthor || 'N/A'}</div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-hashtag text-primary"></i> Call No</div>
            <div className="book-article-view-value font-monospace">{currentData?.callNo || 'N/A'}</div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-info-circle text-primary"></i> Status</div>
            <div className="book-article-view-value">
              <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                {currentData?.articleStatus || 'Active'}
              </span>
            </div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-file-lines text-primary"></i> Page Range</div>
            <div className="book-article-view-value">
              <span className="badge bg-light text-dark border me-2">
                pp. {currentData?.startPageNo || '?'} – {currentData?.endPageNo || '?'}
              </span>
              {pageStats?.valid && (
                <span className="text-muted small">({pageStats.count} pages total)</span>
              )}
            </div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-tags text-primary"></i> Keywords</div>
            <div className="book-article-view-value">
              {keywordTags.length > 0 ? (
                <div className="book-keywords-preview-container">
                  {keywordTags.map((tag, idx) => (
                    <span key={idx} className="book-keyword-chip">
                      <i className="fa fa-hashtag text-primary me-1"></i>{tag}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted">None specified</span>
              )}
            </div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-paragraph text-primary"></i> Abstract / Description</div>
            <div className="book-article-view-value text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {currentData?.articleDescription || 'No abstract or chapter description provided.'}
            </div>
          </div>

          <div className="book-article-view-row">
            <div className="book-article-view-label"><i className="fa fa-clock-rotate-left text-secondary"></i> Metadata</div>
            <div className="book-article-view-value text-muted small">
              Client: <strong>{currentData?.clientID || 'VJCollege'}</strong> | Updated By: <strong>{currentData?.updatedBy || 'Librarian'}</strong> {currentData?.updatedOn ? `on ${new Date(currentData.updatedOn).toLocaleString()}` : ''}
            </div>
          </div>

          <div className="book-article-form-actions">
            <Button variant="secondary" className="btn-book-article-cancel" onClick={handleClose}>
              <i className="fa fa-times me-1"></i> Close
            </Button>
          </div>
        </div>
      ) : (
        /* ========================================================================= */
        /* ADD / EDIT FORM                                                           */
        /* ========================================================================= */
        <Form onSubmit={isNewMode ? handleSubmit : handleUpdate}>
          {/* Section 1: Book & Article Identification */}
          <div className="book-article-section-title">
            <i className="fa fa-id-card"></i> 1. Book Reference & Sequential Article ID
          </div>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <span>
                    Accession No
                    <span className="article-id-badge-tag ms-2 bg-secondary text-white">
                      <i className="fa fa-lock me-1"></i>Locked
                    </span>
                  </span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.accessionNo || activeAccessionNo || ''}
                  disabled={true}
                  readOnly={true}
                  placeholder="Accession No"
                  isInvalid={!!formErrors.accessionNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.accessionNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <span>
                    Book Title
                    <span className="article-id-badge-tag ms-2 bg-secondary text-white">
                      <i className="fa fa-lock me-1"></i>Locked
                    </span>
                  </span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.bookTitle || activeBookTitle || ''}
                  disabled={true}
                  readOnly={true}
                  placeholder="Book Title"
                  isInvalid={!!formErrors.bookTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.bookTitle}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="article-id-disabled-group">
                <Form.Label>
                  <span>
                    Article ID
                    {isNewMode && (
                      <span className="article-id-badge-tag ms-2">
                        <i className="fa fa-magic me-1"></i>Auto-Generated
                      </span>
                    )}
                    {isEditMode && (
                      <span className="article-id-badge-tag ms-2 bg-secondary text-white">
                        <i className="fa fa-lock me-1"></i>Locked
                      </span>
                    )}
                  </span>
                  {isGeneratingId && <Spinner animation="border" size="sm" className="text-primary ms-2" />}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.articleID || ''}
                  disabled={true}
                  readOnly={true}
                  className="article-id-special-input"
                  placeholder={isGeneratingId ? 'Generating sequential ID...' : 'e.g. ACC10023-001'}
                  isInvalid={!!formErrors.articleID}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleID}
                </Form.Control.Feedback>
                {isNewMode && generatedSeq && (
                  <div className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                    <i className="fa fa-check-circle text-success me-1"></i>
                    Article #{generatedSeq} for book Accession No &quot;{currentData?.accessionNo || activeAccessionNo}&quot;
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Section 2: Article Details & Authors */}
          <div className="book-article-section-title mt-4">
            <i className="fa fa-file-lines"></i> 2. Article / Chapter Details
          </div>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Article Title <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.articleTitle || ''}
                  onChange={(e) => handleFieldChange('articleTitle', e.target.value.toUpperCase())}
                  placeholder="Enter main title of the article or chapter"
                  isInvalid={!!formErrors.articleTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleTitle}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group>
                <Form.Label>Article Subtitle</Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.articleSubTitle || ''}
                  onChange={(e) => handleFieldChange('articleSubTitle', e.target.value.toUpperCase())}
                  placeholder="Enter article subtitle (optional)"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Article Author(s) <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.articleAuthor || ''}
                  onChange={(e) => handleFieldChange('articleAuthor', e.target.value.toUpperCase())}
                  placeholder="e.g. Dr. John Doe, Prof. Jane Smith"
                  isInvalid={!!formErrors.articleAuthor}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleAuthor}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Article Status <span className="required-star">*</span>
                </Form.Label>
                <Form.Select
                  value={currentData?.articleStatus || 'Active'}
                  onChange={(e) => handleFieldChange('articleStatus', e.target.value)}
                >
                  {ARTICLE_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Section 3: Indexing, Pagination & Keywords */}
          <div className="book-article-section-title mt-4">
            <i className="fa fa-tags"></i> 3. Classification, Pagination & Subject Keywords
          </div>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Call No <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.callNo || ''}
                  onChange={(e) => handleFieldChange('callNo', e.target.value.toUpperCase())}
                  placeholder="e.g. 230.04 DOE / Shelf Mark"
                  isInvalid={!!formErrors.callNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.callNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4} sm={6}>
              <Form.Group>
                <Form.Label>
                  Start Page No <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={currentData?.startPageNo || ''}
                  onChange={(e) => handleFieldChange('startPageNo', e.target.value)}
                  placeholder="e.g. 1"
                  isInvalid={!!formErrors.startPageNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.startPageNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4} sm={6}>
              <Form.Group>
                <Form.Label>
                  End Page No <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={currentData?.endPageNo || ''}
                  onChange={(e) => handleFieldChange('endPageNo', e.target.value)}
                  placeholder="e.g. 28"
                  isInvalid={!!formErrors.endPageNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.endPageNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Keywords / Subject Terms <span className="required-star">*</span></span>
                  <span
                    className={`badge ${
                      keywordTags.length > MAX_KEYWORDS
                        ? 'bg-danger text-white'
                        : keywordTags.length >= 8
                        ? 'bg-warning text-dark'
                        : 'bg-primary-subtle text-primary border'
                    }`}
                  >
                    {keywordTags.length} / {MAX_KEYWORDS} Max
                  </span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={currentData?.articleKeywords || ''}
                  onChange={(e) => handleFieldChange('articleKeywords', e.target.value.toUpperCase())}
                  placeholder="e.g. DOGMATIC THEOLOGY, REVELATION, CHRISTOLOGY (comma-separated)"
                  isInvalid={!!formErrors.articleKeywords}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleKeywords}
                </Form.Control.Feedback>
                <div className="form-text text-muted" style={{ fontSize: '0.76rem' }}>
                  <i className="fa fa-info-circle me-1 text-primary"></i>
                  Enter up to {MAX_KEYWORDS} concise terms separated by commas. Place sentences and full paragraphs in <strong>Abstract / Description</strong> below.
                </div>
              </Form.Group>
            </Col>

            {/* Live calculation tag & keywords preview */}
            <Col md={12} className="pt-1">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                {pageStats ? (
                  <div className={`book-page-calc-widget ${pageStats.valid ? '' : 'invalid-pages'}`}>
                    <i className={pageStats.valid ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'}></i>
                    {pageStats.message}
                  </div>
                ) : (
                  <span className="text-muted small">
                    <i className="fa fa-info-circle me-1"></i> Enter start and end page numbers to compute page length.
                  </span>
                )}

                {keywordTags.length > 0 && (
                  <div className="book-keywords-preview-container d-flex flex-wrap gap-1 align-items-center">
                    {keywordTags.map((tag, idx) => {
                      const isTooLong = tag.length > 40 || tag.split(/\s+/).length > 5;
                      return (
                        <span
                          key={idx}
                          className={`book-keyword-chip badge ${
                            isTooLong ? 'bg-danger text-white' : 'bg-light text-dark border'
                          }`}
                          style={{ fontSize: '0.78rem' }}
                        >
                          <i className={`fa ${isTooLong ? 'fa-exclamation-triangle me-1' : 'fa-hashtag text-primary me-1'}`}></i>
                          {tag}
                          {isTooLong && <span className="ms-1 fw-bold">(Too long)</span>}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Section 4: Description / Abstract */}
          <div className="book-article-section-title mt-4">
            <i className="fa fa-align-left"></i> 4. Abstract / Description / Chapter Notes
          </div>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Article Abstract / Chapter Summary</span>
                  <span className="text-muted small fw-normal">
                    {(currentData?.articleDescription || '').length} characters
                  </span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={currentData?.articleDescription || ''}
                  onChange={(e) => handleFieldChange('articleDescription', e.target.value)}
                  placeholder="Enter a brief summary, abstract, or scholarly notes about this book article / chapter..."
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Form Actions */}
          <div className="book-article-form-actions">
            <Button
              variant="outline-secondary"
              className="btn-book-article-cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <i className="fa fa-times me-1"></i> Cancel
            </Button>

            {isNewMode && (
              <Button
                variant="primary"
                type="submit"
                className="btn-book-article-submit"
                disabled={isSubmitting || isGeneratingId}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving Article...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-2"></i>
                    Save Article
                  </>
                )}
              </Button>
            )}

            {isEditMode && (
              <Button
                variant="primary"
                type="submit"
                className="btn-book-article-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating Article...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check-circle me-2"></i>
                    Update Article
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </div>
  );
}
